import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { useMemo } from 'react';
import { ToastProvider } from '../components/ui';
import { UnsavedChangesPrompt } from '../components/shared/UnsavedChangesPrompt';
import {
  ParticleBackground,
  LOGIN_CAMERA,
  ROUTE_CAMERAS,
} from '../components/background/ParticleBackground';

export const Route = createRootRoute({
  component: RootComponent,
});

// Get camera settings based on current route
function getCameraForRoute(pathname: string) {
  if (pathname === '/login') {
    return LOGIN_CAMERA;
  }

  // Match route to camera (handle sub-routes like /invoices/new)
  if (pathname === '/' || pathname.startsWith('/dashboard')) {
    return ROUTE_CAMERAS.dashboard;
  }
  if (pathname.startsWith('/customers')) {
    return ROUTE_CAMERAS.customers;
  }
  if (pathname.startsWith('/invoices')) {
    return ROUTE_CAMERAS.invoices;
  }
  if (pathname.startsWith('/contracts')) {
    return ROUTE_CAMERAS.contracts;
  }
  if (pathname.startsWith('/estimates')) {
    return ROUTE_CAMERAS.estimates;
  }
  if (pathname === '/settings/animation') {
    return ROUTE_CAMERAS.animation;
  }
  if (pathname.startsWith('/settings')) {
    return ROUTE_CAMERAS.settings;
  }

  // Default fallback
  return ROUTE_CAMERAS.dashboard;
}

// T522: master animation enable flag. Stored in localStorage; mirrored to
// `prefers-reduced-motion` so a user who opted into OS-level reduced motion
// gets a stable static background even if they never visited /settings.
function isAnimationEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  // OS-level reduced-motion preference takes precedence — it's a strong
  // accessibility signal, not just a default.
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return false;
    }
  } catch {
    /* fall through — assume animation on */
  }
  const raw = window.localStorage.getItem('animation_enabled');
  // Default: enabled. Explicit "0" / "false" disables.
  if (raw === '0' || raw === 'false') return false;
  return true;
}

function RootComponent() {
  const location = useLocation();
  const animationEnabled = useMemo(() => isAnimationEnabled(), []);

  // Load animation settings from localStorage (persists across routes)
  const animationSettings = useMemo(
    () => ({
      speed: parseFloat(localStorage.getItem('animation_speed') || '0.002'),
      particleCount: {
        x: parseInt(localStorage.getItem('animation_particle_x') || '80'),
        z: parseInt(localStorage.getItem('animation_particle_z') || '40'),
      },
      waveAmplitude: parseFloat(localStorage.getItem('animation_wave') || '2.5'),
      noiseIntensity: parseFloat(localStorage.getItem('animation_noise') || '1.2'),
    }),
    []
  );

  // Get camera settings for current route
  const cameraSettings = getCameraForRoute(location.pathname);

  return (
    <ToastProvider>
      {/* T522: Particle Background — only rendered when the master toggle is
          on AND OS-level reduced-motion isn't set. Falls back to a static
          backdrop via CSS (the default body background). */}
      {animationEnabled && <ParticleBackground {...animationSettings} camera={cameraSettings} />}
      <Outlet />
      <UnsavedChangesPrompt />
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </ToastProvider>
  );
}
